import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { Loader2 } from "lucide-react";
import { useTranslation } from "@/i18n/use-translation";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

// The confirmation email links here as ?token_hash=...&type=email&next=<url>.
// Supabase interpolates {{ .RedirectTo }} raw, so `next` is a full URL carrying its own
// query string — it must stay the last param and be read as the raw remainder, not via
// URLSearchParams.
function nextPath(search: string): string {
  const match = search.match(/[?&]next=(.+)$/);
  if (!match) return "/";
  const url = new URL(match[1], window.location.origin);
  if (url.origin !== window.location.origin) return "/";
  return url.pathname + url.search;
}

export default function ConfirmEmailPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const fired = useRef(false);

  // type=email for signup confirmation, type=recovery for password reset — both
  // redeem a single-use token_hash here so an Outlook Safe Links pre-fetch can't burn it.
  const isRecovery = new URLSearchParams(location.search).get("type") === "recovery";

  useEffect(() => {
    // The token is single-use — guard against StrictMode's double effect.
    if (fired.current) return;
    fired.current = true;

    const params = new URLSearchParams(location.search);
    const tokenHash = params.get("token_hash");
    const type = params.get("type");
    if (!tokenHash || (type !== "email" && type !== "recovery")) {
      setError(t("auth.confirmInvalidLink"));
      return;
    }

    supabase.auth.verifyOtp({ type, token_hash: tokenHash }).then(({ error }) => {
      if (error) {
        setError(error.message);
      } else {
        navigate(nextPath(location.search), { replace: true });
      }
    });
  }, [location.search, navigate, t]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-sm">
          <CardHeader>
            <CardTitle className="text-lg font-medium">
              {isRecovery ? t("auth.confirmRecoveryTitle") : t("auth.confirmFailedTitle")}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <p className="text-sm text-muted-foreground">{error}</p>
            <p className="text-sm text-muted-foreground">
              {isRecovery ? t("auth.confirmRecoveryHint") : t("auth.confirmFailedHint")}
            </p>
            <Button asChild className="w-full">
              {isRecovery ? (
                <Link to="/forgot-password">{t("auth.confirmRequestNewLink")}</Link>
              ) : (
                <Link to="/login">{t("auth.confirmGoToSignIn")}</Link>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        {isRecovery ? t("auth.confirmVerifyingRecovery") : t("auth.confirmVerifyingEmail")}
      </div>
    </div>
  );
}
